import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import CloseIcon from '@Images/close_new.svg'
import ErrorIcon from '@Images/error.svg'
import OpenInNewIcon from '@Images/open_in_new.svg'
import autoDownloadUrls from '@Utils/AutoDownloadUrls'
import getRoundedValue from '@Utils/GetRoundedValue'

import {
  IFollowedExportation,
} from '@EditorModules/Exportation/types'

import {
  removeFollowedExportation
} from '@EditorModules/Exportation/actions'

import './ExportationsProgress.scss'

interface ExportationProgressProps {
  readonly userName?: string
  readonly exportation: IFollowedExportation
}

const START_UP_TIME = 30 // seconds
const START_UP_INTERVAL_DELAY = 1000 // 1 second
const MIN_START_UP_RATIO = 0.05
const MAX_START_UP_RATIO = 0.85
const PROGRESS_INTERVAL_DELAY = 300 // 0.3 seconds

const ExportationProgress: React.FC<ExportationProgressProps> = props => {
  const dispatch = useDispatch()

  const {
    userName,
    exportation: {
      id,
      attributes: {
        progress,
        urls,
        error,
        projectTitle,
        projectDuration,
        subtitleId,
        projectVersionId,
      }
    }
  } = props

  const progressBarRef = useRef<HTMLDivElement>(null)
  const startUpIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [startUpPercentage, setStartUpPercentage] = useState(0)
  const [displayProgress, setDisplayProgress] = useState(0)

  const adjustedStartUpRatio = projectDuration ?
    Math.max(
      Math.min(START_UP_TIME / projectDuration, MAX_START_UP_RATIO),
      MIN_START_UP_RATIO
    ) : MIN_START_UP_RATIO

  const fullProgress = urls?.length ?
    100 :
    getRoundedValue(Math.round(startUpPercentage * adjustedStartUpRatio + progress * (1 - adjustedStartUpRatio)), 1)

  const cleanStartUpInterval = () => {
    if (startUpIntervalRef.current) {
      clearInterval(startUpIntervalRef.current)
      startUpIntervalRef.current = null
    }
  }

  const cleanProgressInterval = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
  }

  useEffect(() => {
    const startTime = Date.now()
    const startUpTimeMs = START_UP_TIME * 1000

    startUpIntervalRef.current = setInterval(() => {
      const currentTime = Date.now()
      const elapsed = currentTime - startTime
      const percentage = Math.min(Math.round((elapsed / startUpTimeMs) * 100), 100)
      setStartUpPercentage(percentage)
      if (percentage === 100) {
        cleanStartUpInterval()
      }
    }, START_UP_INTERVAL_DELAY)

    return () => cleanStartUpInterval()
  }, [])

  useEffect(() => {
    cleanProgressInterval()

    if (progress !== 0 && startUpPercentage !== 100) {
      cleanStartUpInterval()
      setStartUpPercentage(100)
    }

    progressIntervalRef.current = setInterval(() => {
      const computedWidth = progressBarRef.current ? window.getComputedStyle(progressBarRef.current).width : '0px'
      const parentWidth = progressBarRef?.current?.parentElement?.offsetWidth ?? 0
      const currentPercent = getRoundedValue((parseFloat(computedWidth) / parentWidth) * 100, 1)

      setDisplayProgress(Math.round(currentPercent))

      if (fullProgress === currentPercent) {
        cleanProgressInterval()
      }
    }, PROGRESS_INTERVAL_DELAY)

    return () => cleanProgressInterval()
  }, [fullProgress])

  const close = () => {
    dispatch(removeFollowedExportation({ exportationId: id }))
  }

  const download = () => {
    if (!urls || urls.length === 0) {
      return
    }

    autoDownloadUrls(urls)
  }

  const contactUs = () => {
    const body = `Hi Support,
    I have encountered an error with the exportation of my project

    ${projectVersionId ? `Project ID: ${projectVersionId}` : ''}${subtitleId ? `Subtitle ID: ${subtitleId}` : ''}${id ? `Exportation ID: ${id}\n` : ''}
    Please help me to unlock my exportation.
    ${userName}
    `
    window.open(`mailto:team@checksub.com?subject=Exportation%20problem&body=${encodeURIComponent(body)}`, '_blank')
  }

  const renderCloseButton = () => {
    return (
      <div
        className="ExportationsProgress__close-button"
        onClick={close}
      >
        <CloseIcon/>
      </div>
    )
  }

  const renderProgress = () => {
    return (
      <div className="ExportationsProgress__progress">
        <div className="ExportationsProgress__progress-frame">
          <div className="ExportationsProgress__progress-frame-text-wrapper">
            <div className="ExportationsProgress__progress-frame-text">
              <div className="ExportationsProgress__progress-frame-text-title">
                Exportation in progress...
              </div>
              <div className="ExportationsProgress__progress-frame-text-subtitle">
                {projectTitle}
              </div>
            </div>
          </div>
          {renderCloseButton()}
        </div>
        <div className="ExportationsProgress__progress-bar">
          <div className="ExportationsProgress__progress-bar-background">
            <div
              className="ExportationsProgress__progress-bar-fill"
              style={{ width: `${fullProgress}%` }}
              ref={progressBarRef}
            >
              <div className="ExportationsProgress__progress-bar-wave-container">
                <div className="ExportationsProgress__progress-bar-wave"/>
              </div>
            </div>
          </div>
        </div>
        <div className="ExportationsProgress__progress-percentage">
          <div className="ExportationsProgress__progress-percentage-pulse-dot"/>
          <div className="ExportationsProgress__progress-percentage-text">
            Processing: {displayProgress}%
          </div>
        </div>
      </div>
    )
  }

  const renderError = () => {
    return (
      <div className="ExportationsProgress__error">
        <div className="ExportationsProgress__error-frame1">
          <div className="ExportationsProgress__error-text">
            <div className="ExportationsProgress__error-frame2">
              <ErrorIcon/>
              <div className="ExportationsProgress__error-frame3">
                <div className="ExportationsProgress__error-text-title">
                  Exportation failed
                </div>
                <div className="ExportationsProgress__error-text-subtitle">
                  If the error persists, please contact us.
                </div>
                <div
                  className="ExportationsProgress__error-link"
                  onClick={contactUs}
                >
                  <div className="ExportationsProgress__error-link-text">
                    Contact us
                  </div>
                  <OpenInNewIcon/>
                </div>
              </div>
            </div>
          </div>
          {renderCloseButton()}
        </div>
      </div>
    )
  }

  const renderDownloadButton = () => {
    return (
      <div className="ExportationsProgress__download">
        <div className="ExportationsProgress__download-frame1">
          <div className="ExportationsProgress__download-text">
            <div className="ExportationsProgress__download-frame2">
              <div className="ExportationsProgress__download-text-title">
                Exportation ready
              </div>
              <div
                className="ExportationsProgress__download-link"
                onClick={download}
              >
                <div className="ExportationsProgress__download-link-text">
                  Download now
                </div>
                <OpenInNewIcon/>
              </div>
            </div>
          </div>
          {renderCloseButton()}
        </div>
      </div>
    )
  }

  const shouldShowDownloadButton = urls && urls.length > 0 && displayProgress === 100
  const shouldShowError = !shouldShowDownloadButton && error
  const shouldShowProgress = !shouldShowDownloadButton && !shouldShowError

  return (
    <div
      className="ExportationProgress"
    >
      {shouldShowProgress && renderProgress()}
      {shouldShowError && renderError()}
      {shouldShowDownloadButton && renderDownloadButton()}
    </div>
  )
}

export default ExportationProgress
